import { Injectable } from '@angular/core';
import { StorageKey } from './appsettings';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@app/environment';
import { catchError, throwError as observableThrowError, tap } from 'rxjs';
import { jwtDecode } from "jwt-decode";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private http: HttpClient,
  ) { }

  /**
   * Returns the auth object
   */
  public currentAuthDetails(): any {
    try {
      // First check session storage
      let authInfo = sessionStorage.getItem(StorageKey.AUTH);
      if (!authInfo) {
        // If not in session, check cookies
        authInfo = this.getCookie(StorageKey.AUTH);
      }

      if (authInfo) {
        try {
          const parsedAuthInfo = JSON.parse(authInfo);
          if (parsedAuthInfo?.access_token?.access_token) {
            try {
              const jwt = jwtDecode(parsedAuthInfo.access_token.access_token);
              if (<number>jwt.exp * 1000 > new Date().getTime()) {
                return parsedAuthInfo;
              }
            } catch (jwtError) {
              console.error('Invalid JWT token:', jwtError);
              this.logout();
            }
          }
        } catch (parseError) {
          console.error('Invalid auth info format:', parseError);
          this.logout();
        }
      }
      return null;
    } catch (error) {
      console.error('Error retrieving auth details:', error);
      return null;
    }
  }

  public login(email: string, password: string, rememberMe: boolean) {
    const url = `${environment.b2bAuthUrl}auth/signin`;
    return this.http.post(url, {
      username: email,
      password
    }).pipe(
      tap((res: any): any => {
        if (res) {
          const authData = JSON.stringify(res);
          if (rememberMe) {
            // Store in cookie for persistent session
            this.setCookie(StorageKey.AUTH, authData, 7); // 7 days expiry
          } else {
            // Store in session storage for temporary session
            sessionStorage.setItem(StorageKey.AUTH, authData);
          }
        }
      }),
      catchError(this.handleError)
    );
  }

  public forgotPassword(email: string) {
    const url = `${environment.b2bAuthUrl}auth/forgot-password`;
    return this.http.post(url, { email }).pipe(
      catchError(this.handleError)
    );
  }

  public logout() {
    // Clear both storage types
    sessionStorage.removeItem(StorageKey.AUTH);
    this.deleteCookie(StorageKey.AUTH);
  }

  private setCookie(name: string, value: string, days: number) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  private getCookie(name: string) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private deleteCookie(name: string) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  private handleError(res: HttpErrorResponse | any) {
    console.error(res.error || res.body.error);
    return observableThrowError(res.error || 'Server error');
  }
}
